import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Medal, UsersThree, CurrencyCircleDollar, CaretDown } from "@phosphor-icons/react";
import { IMAGES } from "@/data/content";
import { useLanguage } from "@/i18n";
import { scrollToSection } from "./Navbar";

const EASE = [0.33, 1, 0.68, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STAT_ICONS = [Medal, UsersThree, CurrencyCircleDollar];

export default function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} data-testid="hero-section" className="relative overflow-hidden lg:min-h-screen">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 scale-110">
        <img src={IMAGES.heroBg} alt="Dubai skyline at night" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-charcoal/60 via-transparent to-charcoal" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 60 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
        className="pointer-events-none relative mx-auto mt-24 w-full max-w-sm sm:max-w-md lg:absolute lg:inset-y-0 lg:right-0 lg:m-0 lg:h-full lg:w-[44%] lg:max-w-none rtl:lg:left-0 rtl:lg:right-auto"
        data-testid="hero-portrait-frame"
      >
        <img
          src={IMAGES.portrait}
          alt="Charbel Moubarak"
          data-testid="hero-headshot"
          className="h-[52vh] w-full object-cover object-top brightness-[0.9] [mask-image:linear-gradient(to_bottom,black_60%,transparent_98%)] lg:h-full lg:object-[50%_18%] lg:[mask-image:linear-gradient(to_right,transparent_0%,black_38%)] rtl:lg:[mask-image:linear-gradient(to_left,transparent_0%,black_38%)]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-transparent to-charcoal/30 lg:bg-gradient-to-r lg:from-charcoal/70 lg:via-transparent lg:to-transparent rtl:lg:bg-gradient-to-l" aria-hidden="true" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-8 pb-24 md:px-12 lg:flex lg:min-h-screen lg:items-center lg:pt-24">
        <div className="w-full text-center lg:max-w-[56%] lg:text-left rtl:lg:ml-auto rtl:lg:text-right">
          <h1 className="font-serif text-5xl uppercase leading-[1.05] tracking-[0.06em] sm:text-6xl lg:text-7xl xl:text-8xl">
            <MaskedLine delay={0.25}>{t.brand.first}</MaskedLine>
            <MaskedLine delay={0.45} className="text-gold-gradient">{t.brand.last}</MaskedLine>
          </h1>

          <MaskedLine delay={0.75} className="mt-6">
            <p data-testid="hero-subheading" className="text-xs uppercase tracking-[0.3em] text-[#A3A3A3] md:text-sm lg:whitespace-nowrap">
              {t.hero.sub1} <span className="mx-2 text-gold">|</span> {t.hero.sub2}
            </p>
          </MaskedLine>

          <motion.div
            data-testid="hero-stats"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.05, ease: EASE }}
            className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-3"
          >
            {t.hero.stats.map((s, i) => {
              const Icon = STAT_ICONS[i];
              return (
                <div
                  key={s.label}
                  data-testid={`hero-stat-${i}`}
                  className="group border border-gold/20 bg-charcoal/60 px-6 py-6 backdrop-blur-md transition-colors duration-500 hover:border-gold/60"
                >
                  <Icon size={26} weight="duotone" className="mx-auto text-gold lg:mx-0 rtl:lg:mx-0" />
                  <p className="mt-3 font-serif text-4xl text-gold-gradient">{s.value}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.25em] text-[#A3A3A3]">{s.label}</p>
                </div>
              );
            })}
          </motion.div>
        </div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={(e) => scrollToSection(e, "#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{ opacity: { delay: 1.8, duration: 1 }, y: { duration: 2, repeat: Infinity } }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold/70 transition-colors duration-300 hover:text-gold"
        aria-label={t.hero.scrollLabel}
      >
        <CaretDown size={28} weight="light" />
      </motion.button>
    </section>
  );
}
